/**
 * Display-only formatting for extracted field values in the Review viewer.
 *
 * DISPLAY ONLY. This never mutates stored values and is never applied to what
 * is sent back to the server (the corrections POST always uses the raw value).
 * Its single job is to make currency render uniformly ($1,234.56) regardless of
 * whether the extraction pathway kept or stripped the OCR "$" and separators.
 *
 * Contract:
 *   - null / undefined / '' → returned as-is (the caller renders the em dash).
 *   - fieldType 'date' → normalized to MM/DD/YYYY (see formatDateValue); parsed
 *     by regex-and-reorder, never a Date object (avoids the UTC-midnight
 *     off-by-one-day shift). Unrecognized shapes pass through unchanged.
 *   - fieldType neither 'currency' nor 'date' → the raw value is returned
 *     unchanged. Quantities and every other type pass through.
 *   - currency: strip whitespace, a single leading currency symbol, and commas;
 *     handle a leading '-' and parenthesised negatives like (1,234.56) → -1234.56.
 *     If the remainder does NOT parse as a finite number, the RAW string is
 *     returned unchanged (never render NaN). Otherwise it is formatted with
 *     Intl.NumberFormat as USD.
 */

const CURRENCY_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

// Month name / 3-letter abbrev → 1-based month number. Explicit lookup so a
// mistyped or non-month word simply misses and the raw string passes through.
const MONTH_INDEX = {
  jan: 1, january: 1,
  feb: 2, february: 2,
  mar: 3, march: 3,
  apr: 4, april: 4,
  may: 5,
  jun: 6, june: 6,
  jul: 7, july: 7,
  aug: 8, august: 8,
  sep: 9, sept: 9, september: 9,
  oct: 10, october: 10,
  nov: 11, november: 11,
  dec: 12, december: 12,
};

function pad2(n) {
  return String(n).padStart(2, '0');
}

// Normalize a date string to MM/DD/YYYY using pure string reordering — NEVER a
// Date object. `new Date("2024-04-07")` parses as UTC midnight and renders one
// day earlier in US timezones; regex-and-reorder avoids that shift entirely.
// Any input that matches no pattern, or whose month/day range is invalid,
// returns the RAW string unchanged (never "Invalid Date"/NaN).
function formatDateValue(rawValue) {
  const raw = String(rawValue);
  const s = raw.trim();

  let mm;
  let dd;
  let yyyy;
  let m;

  if ((m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/))) {
    // Already M/D/Y — just zero-pad.
    mm = Number(m[1]); dd = Number(m[2]); yyyy = m[3];
  } else if ((m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/))) {
    // ISO — reorder to MM/DD/YYYY.
    yyyy = m[1]; mm = Number(m[2]); dd = Number(m[3]);
  } else if ((m = s.match(/^([A-Za-z]{3,})\.?\s+(\d{1,2}),?\s+(\d{4})$/))) {
    // Month NAME or 3-letter abbrev (case-insensitive), e.g. "April 07, 2024".
    const key = m[1].toLowerCase();
    if (!(key in MONTH_INDEX)) return raw;
    mm = MONTH_INDEX[key]; dd = Number(m[2]); yyyy = m[3];
  } else {
    return raw;
  }

  if (mm < 1 || mm > 12 || dd < 1 || dd > 31) return raw;

  return `${pad2(mm)}/${pad2(dd)}/${yyyy}`;
}

export function formatFieldValue(rawValue, fieldType) {
  // Empty-ish values pass straight through — caller owns the em dash.
  if (rawValue === null || rawValue === undefined || rawValue === '') {
    return rawValue;
  }

  // Dates normalize to MM/DD/YYYY (display only; see formatDateValue).
  if (fieldType === 'date') {
    return formatDateValue(rawValue);
  }

  // Only currency is formatted; everything else renders untouched.
  if (fieldType !== 'currency') {
    return rawValue;
  }

  const raw = String(rawValue);
  let s = raw.trim();

  // Parenthesised negative: (1,234.56) → negative.
  let negative = false;
  if (s.startsWith('(') && s.endsWith(')')) {
    negative = true;
    s = s.slice(1, -1).trim();
  }

  // Leading sign.
  if (s.startsWith('-')) {
    negative = true;
    s = s.slice(1).trim();
  } else if (s.startsWith('+')) {
    s = s.slice(1).trim();
  }

  // Strip a single leading currency symbol (e.g. $, €, £, ¥).
  s = s.replace(/^[$€£¥]\s*/, '');

  // Remove thousands separators.
  s = s.replace(/,/g, '').trim();

  const n = Number(s);
  if (s === '' || !Number.isFinite(n)) {
    // Not a number we can format — never render NaN; show the raw value.
    return raw;
  }

  return CURRENCY_FORMATTER.format(negative ? -n : n);
}
